<script setup lang="ts">
  import {
    ref,
    computed,
    watch,
    reactive,
    nextTick,
    getCurrentInstance,
    onMounted,
  } from 'vue';
  import { useModal } from '@/composition/use-modal';
  import { useCoupons } from '@/composition/use-coupons';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useUi } from '@/composition/use-ui'; // For toast notifications
  import { ICoupon } from '@/interfaces/ICoupon';
  import { AlertType } from '@/interfaces/IAlert';
  import V2ModalCouponGeneral from './V2ModalCouponGeneral.vue';
  import V2ModalCouponRestrictions from './V2ModalCouponRestrictions.vue';
  import V2ModalCouponSchedule from './V2ModalCouponSchedule.vue';
  import { utcIsoToDateStringInZone } from '@/utils/date.utils';
  import { COUNTRIES_MAP } from '@/const/countries.const';

  interface Props {
    couponToEdit?: ICoupon;
  }

  const { proxy } = getCurrentInstance();
  const props = defineProps<Props>();
  const emit = defineEmits(['saved']);

  const modals = useModal();
  const couponsHelper = useCoupons();
  const restaurantStore = useRestaurantStore();
  const ui = useUi();

  const mode = computed(() => (props.couponToEdit ? 'edit' : 'create'));
  const title = computed(() =>
    mode.value === 'create' ? 'Nuevo Cupón' : 'Editar Cupón'
  );

  const activeTab = ref(0);
  const form = ref(null);
  const valid = ref(false);
  const saving = ref(false);

  // Tab validation tracking
  interface TabValidation {
    valid: boolean;
    errors: string[];
    touched: boolean;
  }

  const tabValidation = reactive({
    general: {
      valid: true,
      errors: [],
      touched: false,
    } as TabValidation,
    restrictions: {
      valid: true,
      errors: [],
      touched: false,
    } as TabValidation,
    schedule: {
      valid: true,
      errors: [],
      touched: false,
    } as TabValidation,
  });

  // Form data model - use reactive for objects
  const formData = reactive({
    // General tab
    code: '',
    name: '',
    description: '',
    isActive: true,
    isPercentage: true,
    value: 0,
    maxDiscountAmount: null as number | null,

    // Restrictions tab
    validFrom: null as string | null,
    validUntil: null as string | null,
    maxUses: null as number | null,
    maxUsesPerCustomer: null as number | null,
    minOrderAmount: null as number | null,
    maxOrderAmount: null as number | null,
    allowedPaymentMethods: [] as number[],
    allowedOrderTypes: null as string[] | null,

    // Schedule tab
    allowedDays: [] as number[],
    allowedHours: [] as Array<{ from: string; to: string }>,
  });

  // Compute current branch time-zone (fallback to country default)
  const branchTz = computed(
    () =>
      restaurantStore.selectedBranch?.location?.tz ??
      COUNTRIES_MAP.get(restaurantStore.restaurant?.country ?? 'AR')?.tz ??
      'America/Argentina/Buenos_Aires'
  );

  // Initialize form data from existing coupon if editing
  watch(
    () => props.couponToEdit,
    (coupon) => {
      if (coupon) {
        // General
        formData.code = coupon.code;
        formData.name = coupon.name;
        formData.description = coupon.description || '';
        formData.isActive = coupon.isActive;
        formData.isPercentage = coupon.isPercentage;
        formData.value = coupon.value;
        formData.maxDiscountAmount = coupon.maxDiscountAmount;

        // Restrictions
        formData.validFrom = coupon.validFrom
          ? utcIsoToDateStringInZone(coupon.validFrom as any, branchTz.value)
          : null;
        formData.validUntil = coupon.validUntil
          ? utcIsoToDateStringInZone(coupon.validUntil as any, branchTz.value)
          : null;
        formData.maxUses = coupon.maxUses;
        formData.maxUsesPerCustomer = coupon.maxUsesPerCustomer;
        formData.minOrderAmount = coupon.minOrderAmount;
        formData.maxOrderAmount = coupon.maxOrderAmount;
        formData.allowedPaymentMethods = couponsHelper.getPaymentMethodsArray(
          coupon.allowedPaymentMethods
        );
        formData.allowedOrderTypes = coupon.allowedOrderTypes;

        // Schedule
        formData.allowedDays = couponsHelper.getDaysArray(coupon.allowedDays);
        formData.allowedHours = coupon.allowedHours || [];
      }
    },
    { immediate: true }
  );

  // Validation functions for each tab
  function validateGeneral(): boolean {
    const errors: string[] = [];

    // Code validation
    if (!formData.code) {
      errors.push('Código requerido');
    } else if (formData.code.length < 3) {
      errors.push('Código muy corto');
    } else if (!/^[A-Z0-9]+$/.test(formData.code)) {
      errors.push('Código inválido');
    }

    // Name validation
    if (!formData.name) {
      errors.push('Nombre requerido');
    }

    // Value validation
    if (formData.value <= 0) {
      errors.push('Valor debe ser mayor a 0');
    }
    if (formData.isPercentage && formData.value > 100) {
      errors.push('Porcentaje máximo: 100%');
    }

    tabValidation.general.errors = errors;
    tabValidation.general.valid = errors.length === 0;
    return tabValidation.general.valid;
  }

  function validateRestrictions(): boolean {
    const errors: string[] = [];

    // Date validation
    if (formData.validFrom && formData.validUntil) {
      if (new Date(formData.validFrom) > new Date(formData.validUntil)) {
        errors.push('Fechas inválidas');
      }
    }

    // Amount validation
    if (formData.minOrderAmount && formData.maxOrderAmount) {
      if (formData.minOrderAmount > formData.maxOrderAmount) {
        errors.push('Montos inválidos');
      }
    }

    // Number validations
    if (formData.maxUses !== null && formData.maxUses <= 0) {
      errors.push('Usos máximos inválido');
    }
    if (
      formData.maxUsesPerCustomer !== null &&
      formData.maxUsesPerCustomer <= 0
    ) {
      errors.push('Usos por cliente inválido');
    }

    tabValidation.restrictions.errors = errors;
    tabValidation.restrictions.valid = errors.length === 0;
    return tabValidation.restrictions.valid;
  }

  function validateSchedule(): boolean {
    const errors: string[] = [];

    // Time range validation
    for (let i = 0; i < formData.allowedHours.length; i++) {
      const range = formData.allowedHours[i];
      if (!range.from || !range.to) {
        errors.push(`Horario ${i + 1} incompleto`);
      } else if (range.from >= range.to) {
        errors.push(`Horario ${i + 1} inválido`);
      }
    }

    tabValidation.schedule.errors = errors;
    tabValidation.schedule.valid = errors.length === 0;
    return tabValidation.schedule.valid;
  }

  // Run all validations
  function validateAllTabs(): boolean {
    validateGeneral();
    validateRestrictions();
    validateSchedule();

    return (
      tabValidation.general.valid &&
      tabValidation.restrictions.valid &&
      tabValidation.schedule.valid
    );
  }

  // Watch for form data changes and validate
  watch(
    formData,
    () => {
      validateAllTabs();
      // Also trigger Vuetify validation when data changes
      if (form.value) {
        nextTick(() => {
          (form.value as any).validate();
        });
      }
    },
    { deep: true }
  );

  // Mark tab as touched when visited and validate
  watch(
    activeTab,
    async (newTab, oldTab) => {
      if (newTab === 0) tabValidation.general.touched = true;
      if (newTab === 1) tabValidation.restrictions.touched = true;
      if (newTab === 2) tabValidation.schedule.touched = true;

      // Validate form to show field errors when switching tabs
      // Wait for DOM update before validation
      await nextTick();
      await nextTick(); // Double nextTick to ensure tab content is fully rendered

      if (form.value) {
        (form.value as any).validate();
      }
    },
    { immediate: true }
  );

  async function submit() {
    // Mark all tabs as touched
    tabValidation.general.touched = true;
    tabValidation.restrictions.touched = true;
    tabValidation.schedule.touched = true;

    // Validate all tabs
    const isValid = validateAllTabs();

    // Also trigger Vuetify form validation to show field errors
    const formValid = await (form.value as any)?.validate();

    if (!isValid || !formValid) {
      // Find first tab with errors
      let firstErrorTab = -1;
      if (!tabValidation.general.valid) firstErrorTab = 0;
      else if (!tabValidation.restrictions.valid) firstErrorTab = 1;
      else if (!tabValidation.schedule.valid) firstErrorTab = 2;

      // Navigate to first error tab
      if (firstErrorTab !== -1 && firstErrorTab !== activeTab.value) {
        activeTab.value = firstErrorTab;
        await nextTick();
        // Re-validate the form after tab change to show field errors
        await (form.value as any)?.validate();
      }

      // Show error message
      const tabNames = ['General', 'Restricciones', 'Horarios'];
      const errorTabs = [];
      if (!tabValidation.general.valid) errorTabs.push(tabNames[0]);
      if (!tabValidation.restrictions.valid) errorTabs.push(tabNames[1]);
      if (!tabValidation.schedule.valid) errorTabs.push(tabNames[2]);

      ui.showToast(
        AlertType.Error,
        `Por favor corrige los errores en: ${errorTabs.join(', ')}`
      );

      return;
    }

    saving.value = true;

    try {
      const data = {
        ...formData,
        // Ensure null values for optional fields
        description: formData.description || null,
        maxDiscountAmount: formData.isPercentage
          ? formData.maxDiscountAmount
          : null,
        allowedOrderTypes: formData.allowedOrderTypes?.length
          ? formData.allowedOrderTypes
          : null,
        allowedHours: formData.allowedHours.length
          ? formData.allowedHours
          : null,
      };

      let success = false;
      if (mode.value === 'create') {
        const result = await couponsHelper.createCoupon(data);
        success = !!result;
      } else {
        const result = await couponsHelper.updateCoupon(
          props.couponToEdit!.id,
          data
        );
        success = !!result;
      }

      if (success) {
        proxy.$parent.$emit('saved');
        modals.hide('v2-coupon');
      }
    } finally {
      saving.value = false;
    }
  }

  function cancel() {
    modals.hide('v2-coupon');
  }

  // Validation helper
  const canSave = computed(() => {
    return !saving.value;
  });

  // Computed data for child components
  const generalData = computed(() => ({
    code: formData.code,
    name: formData.name,
    description: formData.description,
    isActive: formData.isActive,
    isPercentage: formData.isPercentage,
    value: formData.value,
    maxDiscountAmount: formData.maxDiscountAmount,
  }));

  const restrictionsData = computed(() => ({
    validFrom: formData.validFrom,
    validUntil: formData.validUntil,
    maxUses: formData.maxUses,
    maxUsesPerCustomer: formData.maxUsesPerCustomer,
    minOrderAmount: formData.minOrderAmount,
    maxOrderAmount: formData.maxOrderAmount,
    allowedPaymentMethods: formData.allowedPaymentMethods,
    allowedOrderTypes: formData.allowedOrderTypes,
  }));

  const scheduleData = computed(() => ({
    allowedDays: formData.allowedDays,
    allowedHours: formData.allowedHours,
  }));

  // Tab navigation helpers
  async function nextTab() {
    // Validate current tab before moving to next
    const isValid = await (form.value as any)?.validate();

    if (activeTab.value < 2) {
      activeTab.value++;
    }
  }

  function previousTab() {
    if (activeTab.value > 0) {
      activeTab.value--;
    }
  }

  function updateGeneralData(data: any) {
    console.log('updateGeneralData', data);
    formData.code = data.code;
    formData.name = data.name;
    formData.description = data.description;
    formData.isActive = data.isActive;
    formData.isPercentage = data.isPercentage;
    formData.value = data.value;
    formData.maxDiscountAmount = data.maxDiscountAmount;
  }

  function updateRestrictionsData(data: any) {
    formData.validFrom = data.validFrom;
    formData.validUntil = data.validUntil;
    formData.maxUses = data.maxUses;
    formData.maxUsesPerCustomer = data.maxUsesPerCustomer;
    formData.minOrderAmount = data.minOrderAmount;
    formData.maxOrderAmount = data.maxOrderAmount;
    formData.allowedPaymentMethods = data.allowedPaymentMethods;
    formData.allowedOrderTypes = data.allowedOrderTypes;
  }

  function updateScheduleData(data: any) {
    formData.allowedDays = data.allowedDays;
    formData.allowedHours = data.allowedHours;
  }

  // Get error count for a tab
  function getTabErrorCount(
    tabName: 'general' | 'restrictions' | 'schedule'
  ): number {
    return tabValidation[tabName].touched && !tabValidation[tabName].valid
      ? tabValidation[tabName].errors.length
      : 0;
  }

  // Check if tab has errors (for styling)
  function tabHasErrors(
    tabName: 'general' | 'restrictions' | 'schedule'
  ): boolean {
    return tabValidation[tabName].touched && !tabValidation[tabName].valid;
  }

  // Validate form on mount to ensure proper initial state
  onMounted(async () => {
    await nextTick();
    if (form.value) {
      (form.value as any).resetValidation();
    }
  });
</script>

<template>
  <div class="v2-modal-coupon">
    <div class="modal-header">
      <h3 class="modal-title">{{ title }}</h3>
    </div>

    <v-form
      ref="form"
      v-model="valid"
      @submit.prevent="submit"
      :lazy-validation="false"
    >
      <v-tabs v-model="activeTab" centered class="coupon-tabs">
        <v-tab>
          <v-icon left small>mdi-information</v-icon>
          General
          <v-badge
            v-if="getTabErrorCount('general') > 0"
            :content="getTabErrorCount('general')"
            color="error"
            inline
            class="ml-2"
          />
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-shield-check</v-icon>
          Restricciones
          <v-badge
            v-if="getTabErrorCount('restrictions') > 0"
            :content="getTabErrorCount('restrictions')"
            color="error"
            inline
            class="ml-2"
          />
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-calendar-clock</v-icon>
          Horarios
          <v-badge
            v-if="getTabErrorCount('schedule') > 0"
            :content="getTabErrorCount('schedule')"
            color="error"
            inline
            class="ml-2"
          />
        </v-tab>
      </v-tabs>

      <div class="modal-body">
        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <V2ModalCouponGeneral
              :value="generalData"
              @input="updateGeneralData"
              :mode="mode"
            />
          </v-tab-item>

          <v-tab-item>
            <V2ModalCouponRestrictions
              :value="restrictionsData"
              @input="updateRestrictionsData"
              :currency-symbol="
                restaurantStore.restaurant?.currencySymbol || '$'
              "
            />
          </v-tab-item>

          <v-tab-item>
            <V2ModalCouponSchedule
              :value="scheduleData"
              @input="updateScheduleData"
            />
          </v-tab-item>
        </v-tabs-items>
      </div>

      <div class="modal-footer">
        <div class="footer-left">
          <v-btn v-if="activeTab > 0" text @click="previousTab">
            <v-icon left>mdi-chevron-left</v-icon>
            Anterior
          </v-btn>
        </div>

        <div class="footer-right">
          <v-btn text @click="cancel"> Cancelar </v-btn>

          <v-btn
            v-if="activeTab < 2"
            color="primary"
            @click.stop.prevent="nextTab"
          >
            Siguiente
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>

          <v-btn
            v-else
            color="primary"
            type="submit"
            :disabled="!canSave"
            :loading="saving"
          >
            {{ mode === 'create' ? 'Crear' : 'Guardar' }}
          </v-btn>
        </div>
      </div>
    </v-form>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-coupon {
    .modal-header {
      @apply tw-px-6 tw-pt-6 tw-pb-4;

      .modal-title {
        @apply tw-text-xl tw-font-medium;
      }
    }

    .coupon-tabs {
      @apply tw-mb-4;

      ::v-deep {
        .v-tab {
          &.error--text {
            color: #f44336 !important;
          }
        }

        .v-badge {
          &.v-badge--inline {
            .v-badge__badge {
              @apply tw-relative tw-top-0 tw-right-0;
              position: relative !important;
              font-size: 10px;
              height: 18px;
              min-width: 18px;
              padding: 0 6px;
            }
          }
        }
      }
    }

    .modal-body {
      @apply tw-px-6;
      min-height: 400px;
    }

    .modal-footer {
      @apply tw-px-6 tw-pb-6 tw-pt-4 tw-flex tw-justify-between tw-items-center;

      .footer-left {
        @apply tw-flex-1;
      }

      .footer-right {
        @apply tw-flex tw-space-x-2;
      }
    }
  }
</style>
